<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import {
    getIndexOfPlacement,
    stripAYCode,
  } from '../../utils/ad-ops/adFunctions'
  import { whenConsentSettled } from '../../utils/consent/consentGate'

  interface AssertiveAdAttributes {
    'data-render-registered': boolean
  }
  export interface AssertiveProps {
    placement: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
  }

  const props = withDefaults(defineProps<AssertiveProps>(), {
    tilePosition: -1,
    lazyRender: true,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
  })

  const { adClicked, renderWhenCurrentElementIsVisible } = useAdFunctions()
  const { $ayLoaded, $ayManagerEnv } = useNuxtApp()
  const placementEl = useTemplateRef<HTMLElement | null>('placementEl')
  const placementClassName = ref<string | null>(props.placement)

  const renderAYElement = ref<boolean>(false)
  const adUnitPath = ref<string>('')
  const placementAttributes = computed<AssertiveAdAttributes>(() => {
    return {
      'data-render-registered': false,
    }
  })

  function getEventData(): NtpAdEvent {
    return {
      ntp_tile_position: props.tilePosition,
      ntp_ad_brand: 'programmatic',
      ntp_ad_provider: 'programmatic',
      ntp_creative_id: adUnitPath.value,
      ntp_placement_name: props.placement,
      ntp_ad_refresh: false,
    }
  }

  let adEngaged = false
  let hasClicked = false

  function onMouseOver() {
    adEngaged = true
  }

  function onMouseOut() {
    adEngaged = false
    hasClicked = false
  }

  function onLeaveEvent() {
    if (!adEngaged || hasClicked) return
    hasClicked = true
    adClicked(getEventData(), adUnitPath.value)
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') onLeaveEvent()
  }

  const renderAYAd = async () => {
    if (
      !placementEl.value ||
      placementEl.value?.getAttribute('data-render-registered') === 'true' ||
      renderAYElement.value
    )
      return
    renderAYElement.value = true
    placementEl.value?.setAttribute('data-render-registered', 'true')

    const index = getIndexOfPlacement(props.placement, placementEl.value)

    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    // Hold the ad request — not the markup — until consent settles. Everything
    // above this line is synchronous, so the dedupe guard and the placement
    // class are already applied and nothing about layout moves. Bounded, so a
    // CMP that never answers releases the slot instead of holding inventory.
    //
    // Scope: Assertive, Outbrain and Taboola are gated. Primis and the generic
    // ad components still render ungated.
    await whenConsentSettled()

    // The wait above is bounded but real, so the slot can be gone by the time
    // it resolves — an article click-through or a scroll in a virtualised feed
    // unmounts it mid-wait. Displaying into a detached node would define a slot
    // that never renders and bill it as an unfilled impression.
    if (!placementEl.value?.isConnected) return

    $ayManagerEnv?.cmd.push(() => $ayManagerEnv?.display(props.placement))

    $ayManagerEnv?.cmd.push(() => {
      $ayManagerEnv?.onEvent('afterDefineSlot', (instanceCode: string) => {
        if (stripAYCode(instanceCode) === props.placement) {
          adUnitPath.value = instanceCode
        }
      })
    })
  }

  onMounted(async () => {
    placementEl.value?.addEventListener('mouseenter', onMouseOver, {
      passive: true,
    })
    placementEl.value?.addEventListener('mouseleave', onMouseOut, {
      passive: true,
    })
    window.addEventListener('blur', onLeaveEvent, { passive: true })
    window.addEventListener('pagehide', onLeaveEvent, { passive: true })
    window.addEventListener('beforeunload', onLeaveEvent, { passive: true })
    document.addEventListener('visibilitychange', onVisibilityChange, {
      passive: true,
    })

    await $ayLoaded
    renderWhenCurrentElementIsVisible(
      placementEl.value,
      props.lazyRender,
      props.sticky,
      renderAYAd
    )
  })

  onUnmounted(() => {
    placementEl.value?.removeEventListener('mouseenter', onMouseOver)
    placementEl.value?.removeEventListener('mouseleave', onMouseOut)
    window.removeEventListener('blur', onLeaveEvent)
    window.removeEventListener('pagehide', onLeaveEvent)
    window.removeEventListener('beforeunload', onLeaveEvent)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    renderAYElement.value = false
  })
</script>

<template>
  <div
    :id="props.placement"
    ref="placementEl"
    :class="placementClassName"
    :data-ay-manager-id="props.placement"
    v-bind="placementAttributes"
    :data-ad-instance-id="props.adInstanceId"
  />
</template>
